import { type ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  BellRing,
  ChartColumn,
  CalendarRange,
  ChartPie,
  FileText,
  Gem,
  Lightbulb,
  Wand2,
  ChevronDown,
  Coins,
  CreditCard,
  Crown,
  ExternalLink,
  FolderTree,
  Gift,
  HandCoins,
  Images,
  LayoutDashboard,
  Link2,
  Loader2,
  Mail,
  Medal,
  Newspaper,
  Package,
  PiggyBank,
  Plug,
  ReceiptText,
  RefreshCw,
  Settings,
  Share2,
  ShieldCheck,
  Sparkles,
  Stamp,
  Store,
  Ticket,
  TrendingUp,
  UserCog,
  UserX,
  Users,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import { AppShell, Badge, Button, type NavItem, Spinner, useToast } from '@lp/portal-ui';
import type { BrandSettings, ModuleKey, PortalMe } from '@lp/shared';
import { api, setUnauthenticatedHandler } from '../api.ts';
import { withDefaults } from '../lib/defaults.ts';
import { errorCode, errorMessage, setReadOnlyMode } from '../lib/errors.ts';
import { Notice } from '../components/ui.tsx';
import { type Session, SessionContext, orderLangs } from '../session.tsx';

const icon = 'size-[18px] shrink-0';

/** Pages of plan modules, in nav order; all of them need the platform's own loyalty engine. */
const PROGRAM_MODULE_PAGES: Array<[ModuleKey, NavItem]> = [
  ['pointsPromotions', { to: '/promotions', label: '推廣活動', icon: <Sparkles className={icon} /> }],
  ['referralRewards', { to: '/referral-rewards', label: '推薦獎賞', icon: <Share2 className={icon} /> }],
  ['automations', { to: '/automations', label: '自動提醒', icon: <BellRing className={icon} /> }],
  ['paidMembership', { to: '/paid-membership', label: '付費會籍', icon: <Crown className={icon} /> }],
  ['storedValue', { to: '/stored-value', label: '儲值', icon: <PiggyBank className={icon} /> }],
  ['giftCards', { to: '/gift-cards', label: '電子禮券', icon: <CreditCard className={icon} /> }],
];

function buildNav(s: Session): NavItem[] {
  const { settings, isOwner, isNative, hasModule, shopLimited } = s;
  const nav: NavItem[] = [
    { to: '/', label: '主控台', icon: <LayoutDashboard className={icon} />, section: '總覽' },
    { to: '/settings', label: '網站設定', icon: <Settings className={icon} />, section: '網站內容' },
    { to: '/banners', label: '主頁橫額', icon: <Images className={icon} /> },
    { to: '/news', label: '最新消息', icon: <Newspaper className={icon} /> },
    { to: '/news-categories', label: '消息分類', icon: <FolderTree className={icon} /> },
    { to: '/about', label: '關於我們及分店管理', icon: <Store className={icon} /> },
    // 會員計劃 applies to both data sources; PROAN brands keep their rules in the POS, so the native-only pages are hidden.
    { to: '/levels', label: '會員等級', icon: <Medal className={icon} />, section: '會員計劃' },
    { to: '/points-rules', label: '積分規則', icon: <Coins className={icon} /> },
    // 優惠券 also holds its 派發記錄, 批量優惠碼 and 公開優惠碼 (tabs).
    { to: '/rewards', label: '優惠券', icon: <Ticket className={icon} /> },
  ];
  if (isNative) {
    nav.push(
      { to: '/stamps', label: '印花', icon: <Stamp className={icon} /> },
      { to: '/member-gifts', label: '會員優惠', icon: <Gift className={icon} /> },
      { to: '/loyalty', label: '門市積分', icon: <HandCoins className={icon} /> },
    );
    // The server already leaves native-only modules out for PROAN brands; isNative keeps the nav right with older responses.
    for (const [m, item] of PROGRAM_MODULE_PAGES) if (hasModule(m)) nav.push(item);
  }
  nav.push({ to: '/members', label: '會員名單', icon: <Users className={icon} />, section: '會員' });
  if (hasModule('segments')) nav.push({ to: '/segments', label: '會員分群', icon: <UsersRound className={icon} /> });
  // Own-database brands record sales here (PROAN brands' sales live in the POS); a 保雅 POS can send them too.
  if (isNative) nav.push({ to: '/transactions', label: '交易記錄', icon: <ReceiptText className={icon} /> }, { to: '/pos', label: 'POS 連接', icon: <Plug className={icon} /> });
  // Like 積分換領, the page appears once the brand switches the feature on (Settings > 功能); the page itself explains when it is off.
  if (settings?.features.wallet) nav.push({ to: '/wallet', label: 'Wallet 推送', icon: <WalletCards className={icon} /> });
  if (hasModule('newsletter')) nav.push({ to: '/newsletters', label: '電子報', icon: <Mail className={icon} /> });
  nav.push(
    { to: '/referrals', label: '渠道追蹤', icon: <Link2 className={icon} /> },
    { to: '/deletion-requests', label: '刪除帳戶申請', icon: <UserX className={icon} /> },
    { to: '/reports', label: '報表', icon: <ChartColumn className={icon} />, section: '分析' },
  );
  if (hasModule('analytics')) nav.push({ to: '/analytics', label: '進階分析', icon: <TrendingUp className={icon} /> });
  // Sales by shop only exist where sales are recorded in this platform.
  if (isNative && hasModule('shopScoping')) nav.push({ to: '/shop-report', label: '分店報表', icon: <ChartPie className={icon} /> });
  // AI add-ons with a page of their own (the others are buttons inside existing pages). Those reading sales need this platform's records.
  const ai: NavItem[] = [];
  if (isNative && hasModule('aiCampaignPlanner') && !shopLimited) ai.push({ to: '/ai/campaign-planner', label: 'AI 推廣企劃', icon: <Lightbulb className={icon} /> });
  // The planner and the weekly report cover every shop, so staff limited to some shops don't get them.
  if (hasModule('aiWeeklyReport') && !shopLimited) ai.push({ to: '/ai/weekly-reports', label: 'AI 營運周報', icon: <FileText className={icon} /> });
  if (isNative && hasModule('aiMemberValue')) ai.push({ to: '/ai/member-value', label: 'AI 會員價值', icon: <Gem className={icon} /> });
  if (isNative && hasModule('aiForecast')) ai.push({ to: '/ai/forecast', label: 'AI 客流預測', icon: <CalendarRange className={icon} /> });
  if (hasModule('aiOnboarding')) ai.push({ to: '/ai/setup', label: 'AI 開店助手', icon: <Wand2 className={icon} /> });
  if (ai.length) nav.push({ ...ai[0]!, section: 'AI 助手' }, ...ai.slice(1));
  const account: NavItem[] = [];
  if (isOwner) account.push({ to: '/staff', label: '職員帳戶', icon: <UserCog className={icon} /> });
  account.push({ to: '/plan', label: '我的計劃', icon: <Package className={icon} /> }, { to: '/security', label: '帳戶安全', icon: <ShieldCheck className={icon} /> });
  account[0] = { ...account[0]!, section: '帳戶' };
  nav.push(...account);
  return nav;
}

function FullPage({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-canvas px-4">{children}</div>;
}

/** Signed-in area: loads the staff user and brand settings, then renders the sidebar shell. */
export function PortalLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [me, setMe] = useState<PortalMe | null>(null);
  const [settings, setSettings] = useState<BrandSettings | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const toLogin = useCallback(() => {
    // Read the address at the moment of the 401, not when this callback was created.
    const next = window.location.pathname + window.location.search;
    navigate(next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    setUnauthenticatedHandler(toLogin);
    return () => setUnauthenticatedHandler(null);
  }, [toLogin]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const m = await api.get<PortalMe>('/auth/me');
        if (cancelled) return;
        let s: BrandSettings | null = null;
        try {
          s = await api.get<BrandSettings>('/settings');
        } catch (e) {
          if (errorCode(e) === 'UNAUTHENTICATED') return;
        }
        if (cancelled) return;
        setSettings(s && withDefaults(s));
        setMe(m);
      } catch (e) {
        if (cancelled) return;
        if (errorCode(e) === 'UNAUTHENTICATED') toLogin();
        else setError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt, toLogin]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Signing out locally is enough if the request fails.
    }
    setMe(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const session = useMemo<Session | null>(() => {
    if (!me) return null;
    const enabled = settings?.languages.enabled ?? me.brand.languages;
    const defaultLang = settings?.languages.default ?? me.brand.defaultLang;
    const isAdmin = me.user.role === 'platform_admin';
    // Older API responses may not carry the data source yet; they are all PROAN brands.
    const dataSource = me.brand.dataSource ?? 'proan';
    // Older responses have no plan information: no plan pages until the API sends them.
    const modules = new Set<ModuleKey>(me.entitlements?.modules ?? []);
    const readOnly = me.readOnly === true;
    setReadOnlyMode(readOnly);
    return {
      me,
      settings,
      setSettings: (s: BrandSettings) => setSettings(withDefaults(s)),
      langs: orderLangs(enabled, defaultLang),
      defaultLang,
      isOwner: me.user.role === 'brand_owner' || isAdmin,
      isAdmin,
      dataSource,
      isNative: dataSource === 'native',
      hasModule: (m: ModuleKey) => modules.has(m),
      readOnly,
      stores: me.stores ?? [],
      shopLimited: !!me.user.shopIds,
      logout,
    };
  }, [me, settings, logout]);

  if (error) {
    return (
      <FullPage>
        <div className="max-w-sm rounded-2xl bg-white p-8 text-center ring-1 ring-line">
          <h1 className="text-lg font-medium text-ink">未能載入品牌後台</h1>
          <p className="mt-2 text-sm text-muted">{errorMessage(error)}</p>
          <Button className="mt-5" icon={<RefreshCw className="size-4" />} onClick={() => setAttempt((n) => n + 1)}>
            重新載入
          </Button>
        </div>
      </FullPage>
    );
  }
  if (!session) {
    return (
      <FullPage>
        <Spinner label="載入中…" />
      </FullPage>
    );
  }

  const nav = buildNav(session);
  return (
    <SessionContext.Provider value={session}>
      <AppShell
        subtitle="品牌後台"
        nav={nav}
        currentPath={pathname}
        user={{ name: session.me.user.name, email: session.me.user.email }}
        onLogout={() => void logout()}
        context={
          <div className="flex min-w-0 items-center gap-3">
            {session.me.adminBrands ? <BrandSwitcher me={session.me} /> : <span className="truncate text-[15px] font-medium text-ink">{session.me.brand.name}</span>}
            <a
              href={session.me.brand.memberSiteUrl}
              target="_blank"
              rel="noopener"
              className="hidden shrink-0 items-center gap-1 rounded-full bg-mint px-3 py-1 text-xs font-medium text-ink-soft hover:bg-brand/20 sm:inline-flex"
            >
              會員網站
              <ExternalLink className="size-3.5" />
            </a>
            {session.isAdmin && (
              <span className="ml-auto shrink-0" title={session.readOnly ? '你以平台管理員身份查看此品牌，只可查看，不能更改' : '你以平台管理員身份查看此品牌'}>
                <Badge tone="amber">{session.readOnly ? '平台管理員・唯讀' : '平台管理員'}</Badge>
              </span>
            )}
          </div>
        }
        renderLink={(item, className, onNavigate) => (
          <NavLink to={item.to} end={item.to === '/'} className={({ isActive }) => className(isActive)} onClick={onNavigate}>
            {item.icon}
            <span className="truncate">{item.label}</span>
          </NavLink>
        )}
      >
        {session.readOnly && (
          <Notice tone="warning" title="唯讀模式" className="mb-6">
            你的平台管理員帳戶只可查看此品牌的資料，不能作出任何更改。
          </Notice>
        )}
        <Suspense fallback={<Spinner label="載入中…" />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </SessionContext.Provider>
  );
}

/**
 * Platform admins: the brand name becomes a dropdown of every brand. Switching changes the brand of
 * this session on the server, then reloads the app so every page reads the new brand.
 */
function BrandSwitcher({ me }: { me: PortalMe }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const brands = [...(me.adminBrands ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant'));
  if (!brands.some((b) => b.id === me.brand.id)) brands.unshift({ id: me.brand.id, name: me.brand.name, slug: me.brand.slug, status: 'active' });

  async function pick(brandId: string) {
    if (brandId === me.brand.id) return;
    setBusy(true);
    try {
      await api.post<PortalMe>('/auth/switch-brand', { brandId });
      window.location.assign('/');
    } catch (e) {
      setBusy(false);
      toast.error(errorMessage(e));
    }
  }

  return (
    <label className="relative inline-flex min-w-0 items-center">
      <span className="sr-only">切換品牌</span>
      <select
        value={me.brand.id}
        disabled={busy}
        onChange={(e) => void pick(e.target.value)}
        title="切換品牌"
        className="max-w-[16rem] cursor-pointer appearance-none truncate rounded-full bg-transparent py-1.5 pl-3 pr-8 text-[15px] font-medium text-ink ring-1 ring-inset ring-line transition-colors hover:bg-mint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-wait sm:max-w-xs"
      >
        {brands.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}（{b.slug}）{b.status === 'suspended' ? '・已暫停' : ''}
          </option>
        ))}
      </select>
      {busy ? (
        <Loader2 className="pointer-events-none absolute right-2.5 size-4 animate-spin text-muted" />
      ) : (
        <ChevronDown className="pointer-events-none absolute right-2.5 size-4 text-muted" />
      )}
    </label>
  );
}
